/**
 * Completed Today List - Shows barber's completed tickets for today
 */

import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CheckCircle, DollarSign, Clock, User } from 'lucide-react';

interface CompletedTicket {
  queueId: string;
  clientName: string;
  clientEmail?: string;
  serviceName?: string;
  completedAt: Timestamp;
  actualServiceDuration?: number;
  estimatedWaitTime?: number;
  ticketNumber: string;
}

interface Props {
  barberId: string;
}

export function CompletedTodayList({ barberId }: Props) {
  const [completedTickets, setCompletedTickets] = useState<CompletedTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);

  const loadCompletedToday = useCallback(async () => {
    if (!barberId) {
      console.log('❌ CompletedTodayList: No barberId provided');
      setCompletedTickets([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Get today's start (00:00)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = Timestamp.fromDate(today);

      console.log('🔍 CompletedTodayList: Loading completed tickets', {
        barberId,
        todayStart: today.toISOString(),
      });

      // Query completed tickets for today
      const completedQuery = query(
        collection(db, 'queues'),
        where('barberId', '==', barberId),
        where('status', '==', 'completed'),
        where('completedAt', '>=', todayTimestamp)
      );

      const completedSnapshot = await getDocs(completedQuery);

      console.log('📊 CompletedTodayList: Query result', {
        count: completedSnapshot.size,
        docs: completedSnapshot.docs.map(d => ({
          id: d.id,
          barberId: d.data().barberId,
          status: d.data().status,
          completedAt: d.data().completedAt?.toDate?.()?.toISOString(),
        })),
      });

      const tickets: CompletedTicket[] = completedSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          queueId: doc.id,
          clientName: data.clientName || data.clientEmail || 'Cliente',
          clientEmail: data.clientEmail,
          serviceName: data.serviceName,
          completedAt: data.completedAt,
          actualServiceDuration: data.actualServiceDuration,
          estimatedWaitTime: data.estimatedWaitTime,
          ticketNumber: data.ticketNumber,
        };
      });

      // Sort by completion time (most recent first)
      tickets.sort((a, b) => b.completedAt.toMillis() - a.completedAt.toMillis());

      setCompletedTickets(tickets);

      // Calculate total revenue (placeholder - you can add price field later)
      setTotalRevenue(tickets.length * 15.00); // Example: 15.00€ per cut
    } catch (error) {
      console.error('Error loading completed tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [barberId]);

  useEffect(() => {
    console.log('🔄 CompletedTodayList: useEffect triggered', { barberId });
    loadCompletedToday();
  }, [loadCompletedToday, barberId]);

  if (loading) {
    return (
      <div className="bg-surface border border-hairline rounded-xl p-6">
        <h2 className="font-sans text-lg font-semibold text-ink mb-4">
          Turnos Completados Hoy
        </h2>
        <p className="text-sm text-ink-muted">Cargando…</p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-hairline rounded-xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-sans text-lg font-semibold text-ink flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-success" aria-hidden="true" />
            Turnos Completados Hoy
          </h2>
          <p className="text-sm text-ink-muted mt-1">
            {completedTickets.length} corte{completedTickets.length !== 1 ? 's' : ''} realizado{completedTickets.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 text-ink">
            <DollarSign className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
            <span className="text-2xl font-semibold tabular-nums">
              {totalRevenue.toFixed(2)}€
            </span>
          </div>
          <p className="text-xs text-ink-subtle">Ingreso estimado</p>
        </div>
      </div>

      {/* Tickets List */}
      {completedTickets.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="w-10 h-10 text-ink-subtle mx-auto mb-3" aria-hidden="true" />
          <p className="text-ink-muted">
            No hay turnos completados hoy todavía
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {completedTickets.map((ticket) => {
            const completedTime = ticket.completedAt.toDate();
            const timeStr = completedTime.toLocaleTimeString('es-AR', {
              hour: '2-digit',
              minute: '2-digit',
            });

            return (
              <div
                key={ticket.queueId}
                className="p-4 bg-surface-2 border border-hairline rounded-lg hover:border-hairline-strong transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Client Info */}
                    <div className="flex flex-col gap-1 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
                        <span className="font-semibold text-ink">
                          {ticket.clientName}
                        </span>
                        <span className="text-xs text-ink-subtle font-mono">
                          #{ticket.ticketNumber}
                        </span>
                      </div>
                      {ticket.clientEmail && (
                        <span className="text-xs text-ink-muted ml-6">
                          {ticket.clientEmail}
                        </span>
                      )}
                    </div>

                    {/* Service Name */}
                    {ticket.serviceName && (
                      <p className="text-sm text-ink-muted mb-2">
                        {ticket.serviceName}
                      </p>
                    )}

                    {/* Duration */}
                    {ticket.actualServiceDuration && (
                      <div className="flex items-center gap-1 text-xs text-ink-subtle tabular-nums">
                        <Clock className="w-3 h-3" aria-hidden="true" />
                        <span>Duración: {ticket.actualServiceDuration} min</span>
                      </div>
                    )}
                  </div>

                  {/* Completion Time */}
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm font-medium text-success tabular-nums">
                      <CheckCircle className="w-4 h-4" aria-hidden="true" />
                      {timeStr}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Stats */}
      {completedTickets.length > 0 && (
        <div className="mt-6 pt-6 border-t border-hairline">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-semibold text-ink tabular-nums">
                {completedTickets.length}
              </p>
              <p className="text-xs text-ink-muted">Cortes Totales</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-ink tabular-nums">
                {Math.round(
                  completedTickets.reduce((sum, t) => sum + (t.actualServiceDuration || 0), 0) /
                    completedTickets.length
                )}m
              </p>
              <p className="text-xs text-ink-muted">Duración Promedio</p>
            </div>
            <div className="text-center col-span-2 md:col-span-1">
              <p className="text-2xl font-semibold text-ink tabular-nums">
                {(totalRevenue / completedTickets.length).toFixed(2)}€
              </p>
              <p className="text-xs text-ink-muted">Ingreso Promedio</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
